/**
 * Process Builder Storage Service
 *
 * Manages process definitions and execution history in localStorage.
 * Provides full CRUD operations for processes, version control,
 * and execution tracking.
 *
 * Phase 1: localStorage (mock)
 * Phase 2: Real API calls
 *
 * @module services/processBuilderStorage
 */

import {
  Process,
  ProcessVersion,
  ProcessExecution,
  ProcessCriteriaGroup,
  ProcessAction,
  ScheduledAction,
  ProcessBuilderStats,
  SingleProcessStats,
  CreateProcessPayload,
  UpdateProcessPayload,
  CloneProcessPayload,
  ProcessSummary,
  ProcessConfigSnapshot,
  ProcessCriteriaGroupInput,
  ProcessActionInput,
  ScheduledActionInput
} from '../types/processBuilder';

// Storage keys
const STORAGE_KEY_PROCESSES = 'crm_processes';
const STORAGE_KEY_EXECUTIONS = 'crm_process_executions';

/**
 * Generate unique ID with prefix
 */
const generateId = (prefix: string): string => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get current ISO timestamp
 */
const now = (): string => new Date().toISOString();

/**
 * Calculate milliseconds between two dates
 */
const msBetween = (date1: string, date2: string): number => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return Math.abs(d2.getTime() - d1.getTime());
};

/**
 * Check if date is today
 */
const isToday = (dateStr: string): boolean => {
  const date = new Date(dateStr);
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Generate API name from display name
 */
const generateApiName = (name: string): string => {
  return name
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .toLowerCase()
    .substring(0, 50);
};

/**
 * Add IDs to criteria groups if missing
 */
const ensureCriteriaGroupIds = (groups: ProcessCriteriaGroupInput[]): ProcessCriteriaGroup[] => {
  return groups.map((group, index) => ({
    ...group,
    id: group.id || generateId('cg'),
    conditions: group.conditions.map(cond => ({
      ...cond,
      id: cond.id || generateId('cond')
    })),
    order: group.order ?? index
  }));
};

/**
 * Add IDs to actions if missing
 */
const ensureActionIds = (actions: ProcessActionInput[]): ProcessAction[] => {
  return actions.map((action, index) => ({
    ...action,
    id: action.id || generateId('act'),
    order: action.order ?? index
  }));
};

/**
 * Add IDs to scheduled actions if missing
 */
const ensureScheduledActionIds = (
  scheduledActions: ScheduledActionInput[]
): ScheduledAction[] => {
  return scheduledActions.map(sa => ({
    ...sa,
    id: sa.id || generateId('sa'),
    actions: ensureActionIds(sa.actions)
  }));
};

/**
 * Process Builder Storage Service
 */
class ProcessBuilderStorage {
  // ============================================
  // PROCESS CRUD OPERATIONS
  // ============================================

  /**
   * Get all processes
   */
  getAllProcesses(): Process[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY_PROCESSES);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error reading processes:', error);
      return [];
    }
  }

  /**
   * Get a single process by ID
   */
  getProcess(processId: string): Process | null {
    const processes = this.getAllProcesses();
    return processes.find(p => p.id === processId) || null;
  }

  /**
   * Get process by API name
   */
  getProcessByApiName(apiName: string): Process | null {
    const processes = this.getAllProcesses();
    return processes.find(p => p.apiName === apiName) || null;
  }

  /**
   * Get processes for a specific object type
   */
  getProcessesForObject(objectType: string, activeOnly = true): Process[] {
    const processes = this.getAllProcesses();
    return processes
      .filter(p => p.objectType === objectType && (!activeOnly || p.isActive))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Get processes by category
   */
  getProcessesByCategory(category: string): Process[] {
    const processes = this.getAllProcesses();
    return processes.filter(p => p.category === category);
  }

  /**
   * Get process summaries for list view
   */
  getProcessSummaries(): ProcessSummary[] {
    const processes = this.getAllProcesses();
    return processes.map(p => this.toSummary(p));
  }

  /**
   * Convert process to summary
   */
  private toSummary(process: Process): ProcessSummary {
    const actionCount = Object.values(process.immediateActions).reduce(
      (sum, actions) => sum + actions.length,
      0
    );
    const scheduledActionCount = Object.values(process.scheduledActions).reduce(
      (sum, scheduled) => sum + scheduled.length,
      0
    );

    return {
      id: process.id,
      name: process.name,
      description: process.description,
      objectType: process.objectType,
      triggerType: process.triggerType,
      isActive: process.isActive,
      status: process.status,
      category: process.category,
      criteriaGroupCount: process.criteriaGroups.length,
      actionCount,
      scheduledActionCount,
      currentVersion: process.currentVersion,
      lastModifiedDate: process.lastModifiedDate,
      lastModifiedByName: process.lastModifiedByName,
      executionCount: process.executionCount,
      successRate:
        process.executionCount && process.executionCount > 0
          ? Math.round(((process.successCount || 0) / process.executionCount) * 100)
          : undefined
    };
  }

  /**
   * Create a new process
   */
  createProcess(payload: CreateProcessPayload): Process {
    const processes = this.getAllProcesses();
    const timestamp = now();

    // Generate API name if not provided
    const apiName = payload.apiName || generateApiName(payload.name);

    // Ensure unique API name
    let uniqueApiName = apiName;
    let suffix = 1;
    while (processes.some(p => p.apiName === uniqueApiName)) {
      uniqueApiName = `${apiName}_${suffix}`;
      suffix++;
    }

    // Process criteria groups
    const criteriaGroups = payload.criteriaGroups
      ? ensureCriteriaGroupIds(payload.criteriaGroups)
      : [];

    // Process entry criteria
    const entryCriteria = payload.entryCriteria
      ? {
          ...payload.entryCriteria,
          id: generateId('ec'),
          conditions: payload.entryCriteria.conditions.map(c => ({
            ...c,
            id: c.id || generateId('cond')
          }))
        }
      : undefined;

    // Process immediate actions
    const immediateActions: Record<string, ProcessAction[]> = {};
    if (payload.immediateActions) {
      for (const [groupId, actions] of Object.entries(payload.immediateActions)) {
        immediateActions[groupId] = ensureActionIds(actions);
      }
    }

    // Process scheduled actions
    const scheduledActions: Record<string, ScheduledAction[]> = {};
    if (payload.scheduledActions) {
      for (const [groupId, scheduled] of Object.entries(payload.scheduledActions)) {
        scheduledActions[groupId] = ensureScheduledActionIds(scheduled);
      }
    }

    // Create initial version
    const initialVersion: ProcessVersion = {
      id: generateId('pv'),
      versionNumber: 1,
      status: 'draft',
      createdDate: timestamp,
      createdBy: 'current_user',
      description: 'Initial version'
    };

    const newProcess: Process = {
      id: generateId('proc'),
      name: payload.name,
      description: payload.description,
      apiName: uniqueApiName,
      objectType: payload.objectType,
      triggerType: payload.triggerType,
      isActive: payload.isActive ?? false,
      status: payload.isActive ? 'active' : 'draft',
      category: payload.category,
      entryCriteria,
      reevaluateOnUpdate: payload.reevaluateOnUpdate ?? false,
      criteriaGroups,
      immediateActions,
      scheduledActions,
      currentVersion: 1,
      versions: [initialVersion],
      createdDate: timestamp,
      createdBy: 'current_user',
      createdByName: 'System Administrator',
      lastModifiedDate: timestamp,
      lastModifiedBy: 'current_user',
      lastModifiedByName: 'System Administrator',
      executionCount: 0,
      successCount: 0,
      failureCount: 0
    };

    processes.push(newProcess);
    this.saveProcesses(processes);

    return newProcess;
  }

  /**
   * Update an existing process
   */
  updateProcess(payload: UpdateProcessPayload): Process | null {
    const processes = this.getAllProcesses();
    const index = processes.findIndex(p => p.id === payload.id);

    if (index === -1) return null;

    const existing = processes[index];
    const timestamp = now();

    // Handle status changes
    let status = payload.status ?? existing.status;
    if (payload.isActive !== undefined) {
      status = payload.isActive ? 'active' : 'inactive';
    }

    // Extract payload properties that need special handling (they have Input types with optional IDs)
    const {
      entryCriteria: payloadEntryCriteria,
      criteriaGroups: payloadCriteriaGroups,
      immediateActions: payloadImmediateActions,
      scheduledActions: payloadScheduledActions,
      ...safePayloadProps
    } = payload;

    // Start with existing process and overlay safe payload properties
    const updated: Process = {
      ...existing,
      ...safePayloadProps,
      status,
      lastModifiedDate: timestamp,
      lastModifiedBy: 'current_user',
      lastModifiedByName: 'System Administrator'
    };

    // If entryCriteria was updated, ensure it has an ID
    if (payloadEntryCriteria) {
      const processedCriteria = ensureCriteriaGroupIds([payloadEntryCriteria])[0];
      updated.entryCriteria = processedCriteria;
    }

    // If criteria groups were updated, ensure IDs
    if (payloadCriteriaGroups) {
      updated.criteriaGroups = ensureCriteriaGroupIds(payloadCriteriaGroups);
    }

    // If immediate actions were updated, ensure IDs
    if (payloadImmediateActions) {
      const processedActions: Record<string, ProcessAction[]> = {};
      for (const [groupId, actions] of Object.entries(payloadImmediateActions)) {
        processedActions[groupId] = ensureActionIds(actions);
      }
      updated.immediateActions = processedActions;
    }

    // If scheduled actions were updated, ensure IDs
    if (payloadScheduledActions) {
      const processedScheduled: Record<string, ScheduledAction[]> = {};
      for (const [groupId, scheduled] of Object.entries(payloadScheduledActions)) {
        processedScheduled[groupId] = ensureScheduledActionIds(scheduled);
      }
      updated.scheduledActions = processedScheduled;
    }

    processes[index] = updated;
    this.saveProcesses(processes);

    return updated;
  }

  /**
   * Delete a process
   */
  deleteProcess(processId: string): boolean {
    const processes = this.getAllProcesses();
    const filtered = processes.filter(p => p.id !== processId);

    if (filtered.length === processes.length) return false;

    this.saveProcesses(filtered);

    // Also delete associated executions
    const executions = this.getAllExecutions();
    const filteredExecutions = executions.filter(e => e.processId !== processId);
    this.saveExecutions(filteredExecutions);

    return true;
  }

  // ============================================
  // STATUS MANAGEMENT
  // ============================================

  /**
   * Activate a process
   */
  activateProcess(processId: string): Process | null {
    const process = this.getProcess(processId);
    if (!process) return null;

    // Create new version if current is draft
    const currentVersion = process.versions.find(
      v => v.versionNumber === process.currentVersion
    );

    if (currentVersion && currentVersion.status === 'draft') {
      currentVersion.status = 'active';
      currentVersion.activatedDate = now();
      currentVersion.activatedBy = 'current_user';

      // Create config snapshot
      currentVersion.configSnapshot = JSON.stringify({
        entryCriteria: process.entryCriteria,
        criteriaGroups: process.criteriaGroups,
        immediateActions: process.immediateActions,
        scheduledActions: process.scheduledActions
      } as ProcessConfigSnapshot);
    }

    return this.updateProcess({
      id: processId,
      isActive: true,
      status: 'active'
    });
  }

  /**
   * Deactivate a process
   */
  deactivateProcess(processId: string): Process | null {
    const process = this.getProcess(processId);
    if (!process) return null;

    // Mark current version as inactive
    const currentVersion = process.versions.find(
      v => v.versionNumber === process.currentVersion
    );

    if (currentVersion && currentVersion.status === 'active') {
      currentVersion.status = 'inactive';
      currentVersion.deactivatedDate = now();
      currentVersion.deactivatedBy = 'current_user';
    }

    return this.updateProcess({
      id: processId,
      isActive: false,
      status: 'inactive'
    });
  }

  /**
   * Clone a process
   */
  cloneProcess(payload: CloneProcessPayload): Process | null {
    const original = this.getProcess(payload.sourceProcessId);
    if (!original) return null;

    // Strip IDs from criteria groups
    const strippedCriteriaGroups = original.criteriaGroups.map(cg => {
      const { id: _cgId, ...cgRest } = cg;
      return {
        ...cgRest,
        conditions: cg.conditions.map(c => {
          const { id: _cId, ...cRest } = c;
          return cRest;
        })
      };
    });

    // Strip ID from entry criteria if present
    const strippedEntryCriteria = original.entryCriteria
      ? (() => {
          const { id: _ecId, ...ecRest } = original.entryCriteria;
          return {
            ...ecRest,
            conditions: original.entryCriteria.conditions.map(c => {
              const { id: _cId, ...cRest } = c;
              return cRest;
            })
          };
        })()
      : undefined;

    // Strip IDs from immediate actions
    const strippedImmediateActions = Object.fromEntries(
      Object.entries(original.immediateActions).map(([key, actions]) => [
        key,
        actions.map(a => {
          const { id: _aId, ...aRest } = a;
          return aRest;
        })
      ])
    );

    // Strip IDs from scheduled actions
    const strippedScheduledActions = payload.includeScheduledActions
      ? Object.fromEntries(
          Object.entries(original.scheduledActions).map(([key, scheduled]) => [
            key,
            scheduled.map(sa => {
              const { id: _saId, ...saRest } = sa;
              return {
                ...saRest,
                actions: sa.actions.map(a => {
                  const { id: _aId, ...aRest } = a;
                  return aRest;
                })
              };
            })
          ])
        )
      : undefined;

    const clonePayload: CreateProcessPayload = {
      name: payload.newName,
      description: payload.description || `Clone of ${original.name}`,
      objectType: original.objectType,
      triggerType: original.triggerType,
      isActive: false,
      category: original.category,
      entryCriteria: strippedEntryCriteria,
      reevaluateOnUpdate: original.reevaluateOnUpdate,
      criteriaGroups: strippedCriteriaGroups,
      immediateActions: strippedImmediateActions,
      scheduledActions: strippedScheduledActions
    };

    return this.createProcess(clonePayload);
  }

  // ============================================
  // VERSION CONTROL
  // ============================================

  /**
   * Create a new version of a process
   */
  createNewVersion(processId: string, description?: string): ProcessVersion | null {
    const process = this.getProcess(processId);
    if (!process) return null;

    const newVersionNumber = process.currentVersion + 1;
    const timestamp = now();

    const newVersion: ProcessVersion = {
      id: generateId('pv'),
      versionNumber: newVersionNumber,
      status: 'draft',
      createdDate: timestamp,
      createdBy: 'current_user',
      description: description || `Version ${newVersionNumber}`
    };

    process.versions.push(newVersion);
    process.currentVersion = newVersionNumber;
    process.lastModifiedDate = timestamp;
    process.lastModifiedBy = 'current_user';
    process.lastModifiedByName = 'System Administrator';

    this.saveProcess(process);

    return newVersion;
  }

  /**
   * Activate a specific version of a process
   */
  activateVersion(processId: string, versionId: string): Process | null {
    const process = this.getProcess(processId);
    if (!process) return null;

    const version = process.versions.find(v => v.id === versionId);
    if (!version) return null;

    const timestamp = now();

    // Deactivate current active version
    const currentActive = process.versions.find(v => v.status === 'active');
    if (currentActive) {
      currentActive.status = 'inactive';
      currentActive.deactivatedDate = timestamp;
      currentActive.deactivatedBy = 'current_user';
    }

    // Activate the selected version
    version.status = 'active';
    version.activatedDate = timestamp;
    version.activatedBy = 'current_user';

    // If version has a config snapshot, restore it
    if (version.configSnapshot) {
      try {
        const snapshot: ProcessConfigSnapshot = JSON.parse(version.configSnapshot);
        process.entryCriteria = snapshot.entryCriteria;
        process.criteriaGroups = snapshot.criteriaGroups;
        process.immediateActions = snapshot.immediateActions;
        process.scheduledActions = snapshot.scheduledActions;
      } catch (error) {
        console.error('Error restoring version snapshot:', error);
      }
    }

    process.currentVersion = version.versionNumber;
    process.isActive = true;
    process.status = 'active';
    process.lastModifiedDate = timestamp;
    process.lastModifiedBy = 'current_user';
    process.lastModifiedByName = 'System Administrator';

    this.saveProcess(process);

    return process;
  }

  /**
   * Get all versions for a process
   */
  getProcessVersions(processId: string): ProcessVersion[] {
    const process = this.getProcess(processId);
    if (!process) return [];
    return process.versions.sort((a, b) => b.versionNumber - a.versionNumber);
  }

  // ============================================
  // EXECUTION TRACKING
  // ============================================

  /**
   * Get all executions
   */
  getAllExecutions(): ProcessExecution[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY_EXECUTIONS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error reading executions:', error);
      return [];
    }
  }

  /**
   * Get executions for a specific process
   */
  getExecutionsForProcess(processId: string, limit = 50): ProcessExecution[] {
    const executions = this.getAllExecutions();
    return executions
      .filter(e => e.processId === processId)
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
      .slice(0, limit);
  }

  /**
   * Get executions for a specific record
   */
  getExecutionsForRecord(recordId: string): ProcessExecution[] {
    const executions = this.getAllExecutions();
    return executions
      .filter(e => e.recordId === recordId)
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
  }

  /**
   * Record a new execution
   */
  recordExecution(execution: Omit<ProcessExecution, 'id'>): ProcessExecution {
    const executions = this.getAllExecutions();

    const newExecution: ProcessExecution = {
      ...execution,
      id: generateId('exec')
    };

    executions.push(newExecution);
    this.saveExecutions(executions);

    // Update process stats
    const process = this.getProcess(execution.processId);
    if (process) {
      process.executionCount = (process.executionCount || 0) + 1;
      process.lastRunDate = execution.startTime;

      if (execution.status === 'completed') {
        process.successCount = (process.successCount || 0) + 1;
      } else if (execution.status === 'failed') {
        process.failureCount = (process.failureCount || 0) + 1;
      }

      this.saveProcess(process);
    }

    return newExecution;
  }

  /**
   * Update an execution (e.g., when it completes)
   */
  updateExecution(
    executionId: string,
    updates: Partial<ProcessExecution>
  ): ProcessExecution | null {
    const executions = this.getAllExecutions();
    const index = executions.findIndex(e => e.id === executionId);

    if (index === -1) return null;

    executions[index] = { ...executions[index], ...updates };
    this.saveExecutions(executions);

    return executions[index];
  }

  // ============================================
  // STATISTICS
  // ============================================

  /**
   * Get overall statistics
   */
  getStats(): ProcessBuilderStats {
    const processes = this.getAllProcesses();
    const executions = this.getAllExecutions();

    const activeProcesses = processes.filter(p => p.isActive).length;
    const inactiveProcesses = processes.filter(p => !p.isActive && p.status === 'inactive').length;
    const draftProcesses = processes.filter(p => p.status === 'draft').length;

    const successfulExecutions = executions.filter(e => e.status === 'completed').length;
    const failedExecutions = executions.filter(e => e.status === 'failed').length;
    const executionsToday = executions.filter(e => isToday(e.startTime)).length;

    const completedExecutions = executions.filter(
      e => e.endTime && (e.status === 'completed' || e.status === 'failed')
    );
    const avgExecutionTime =
      completedExecutions.length > 0
        ? completedExecutions.reduce(
            (sum, e) => sum + (e.duration || msBetween(e.startTime, e.endTime!)),
            0
          ) / completedExecutions.length
        : 0;

    const byObjectType: Record<string, number> = {};
    const byTriggerType: Record<string, number> = {};

    for (const process of processes) {
      byObjectType[process.objectType] = (byObjectType[process.objectType] || 0) + 1;
      byTriggerType[process.triggerType] = (byTriggerType[process.triggerType] || 0) + 1;
    }

    return {
      totalProcesses: processes.length,
      activeProcesses,
      inactiveProcesses,
      draftProcesses,
      totalExecutions: executions.length,
      successfulExecutions,
      failedExecutions,
      executionsToday,
      successRate:
        executions.length > 0 ? Math.round((successfulExecutions / executions.length) * 100) : 0,
      avgExecutionTime: Math.round(avgExecutionTime),
      byObjectType,
      byTriggerType: byTriggerType as Record<any, number>
    };
  }

  /**
   * Get statistics for a single process
   */
  getProcessStats(processId: string): SingleProcessStats | null {
    const process = this.getProcess(processId);
    if (!process) return null;

    const executions = this.getExecutionsForProcess(processId, 1000);

    const successCount = executions.filter(e => e.status === 'completed').length;
    const failureCount = executions.filter(e => e.status === 'failed').length;

    const completedExecutions = executions.filter(
      e => e.endTime && (e.status === 'completed' || e.status === 'failed')
    );
    const avgExecutionTime =
      completedExecutions.length > 0
        ? completedExecutions.reduce(
            (sum, e) => sum + (e.duration || msBetween(e.startTime, e.endTime!)),
            0
          ) / completedExecutions.length
        : 0;

    // Find most common failure reason
    const failureReasons = executions
      .filter(e => e.status === 'failed' && e.errorMessage)
      .map(e => e.errorMessage!);
    const reasonCounts = failureReasons.reduce((acc, reason) => {
      acc[reason] = (acc[reason] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const topFailureReason = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1])[0]?.[0];

    const lastExecution = executions[0];

    return {
      processId: process.id,
      processName: process.name,
      totalExecutions: executions.length,
      successCount,
      failureCount,
      successRate:
        executions.length > 0 ? Math.round((successCount / executions.length) * 100) : 0,
      avgExecutionTime: Math.round(avgExecutionTime),
      lastExecuted: lastExecution?.startTime,
      topFailureReason
    };
  }

  // ============================================
  // SEARCH & FILTER
  // ============================================

  /**
   * Search processes by name or description
   */
  searchProcesses(query: string): Process[] {
    const processes = this.getAllProcesses();
    const lowerQuery = query.toLowerCase();

    return processes.filter(
      p =>
        p.name.toLowerCase().includes(lowerQuery) ||
        p.description.toLowerCase().includes(lowerQuery) ||
        p.apiName?.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Get processes with filters
   */
  getFilteredProcesses(filters: {
    objectType?: string;
    triggerType?: string;
    status?: string;
    category?: string;
    isActive?: boolean;
  }): Process[] {
    let processes = this.getAllProcesses();

    if (filters.objectType) {
      processes = processes.filter(p => p.objectType === filters.objectType);
    }
    if (filters.triggerType) {
      processes = processes.filter(p => p.triggerType === filters.triggerType);
    }
    if (filters.status) {
      processes = processes.filter(p => p.status === filters.status);
    }
    if (filters.category) {
      processes = processes.filter(p => p.category === filters.category);
    }
    if (filters.isActive !== undefined) {
      processes = processes.filter(p => p.isActive === filters.isActive);
    }

    return processes;
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Save all processes to localStorage
   */
  private saveProcesses(processes: Process[]): void {
    try {
      localStorage.setItem(STORAGE_KEY_PROCESSES, JSON.stringify(processes));
    } catch (error) {
      console.error('Error saving processes:', error);
    }
  }

  /**
   * Save a single process (update in array and persist)
   */
  private saveProcess(process: Process): void {
    const processes = this.getAllProcesses();
    const index = processes.findIndex(p => p.id === process.id);

    if (index >= 0) {
      processes[index] = process;
    } else {
      processes.push(process);
    }

    this.saveProcesses(processes);
  }

  /**
   * Save all executions to localStorage
   */
  private saveExecutions(executions: ProcessExecution[]): void {
    try {
      localStorage.setItem(STORAGE_KEY_EXECUTIONS, JSON.stringify(executions));
    } catch (error) {
      console.error('Error saving executions:', error);
    }
  }

  /**
   * Initialize with mock data if empty
   */
  initializeWithMockData(processes: Process[], executions?: ProcessExecution[]): void {
    if (this.getAllProcesses().length === 0 && processes.length > 0) {
      this.saveProcesses(processes);
      console.log(`📦 Initialized ${processes.length} mock processes`);
    }
    if (executions && this.getAllExecutions().length === 0 && executions.length > 0) {
      this.saveExecutions(executions);
      console.log(`📦 Initialized ${executions.length} mock executions`);
    }
  }

  /**
   * Clear all data (for testing)
   */
  clearAll(): void {
    localStorage.removeItem(STORAGE_KEY_PROCESSES);
    localStorage.removeItem(STORAGE_KEY_EXECUTIONS);
    console.log('🗑️ Cleared all process builder data');
  }

  /**
   * Export all data (for backup)
   */
  exportData(): { processes: Process[]; executions: ProcessExecution[] } {
    return {
      processes: this.getAllProcesses(),
      executions: this.getAllExecutions()
    };
  }

  /**
   * Import data (for restore)
   */
  importData(data: { processes: Process[]; executions?: ProcessExecution[] }): void {
    if (data.processes) {
      this.saveProcesses(data.processes);
    }
    if (data.executions) {
      this.saveExecutions(data.executions);
    }
  }
}

// Export singleton instance
export const processBuilderStorage = new ProcessBuilderStorage();
